import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ClipboardCheck,
  Eye,
  GripVertical,
  HelpCircle,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useBlocker, useNavigate, useParams } from 'react-router'

import { Button } from '@/components/elements/button'
import { Checkbox } from '@/components/elements/checkbox'
import { Input } from '@/components/elements/input'
import { Label } from '@/components/elements/label'
import { RadioGroup, RadioGroupItem } from '@/components/elements/radio-group'
import { Separator } from '@/components/elements/separator'
import { Switch } from '@/components/elements/switch'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { Layout } from '@/components/Layout'
import { addEAP, getEAP, updateEAP } from '@/services/eap.service'
import { getEntityJobs } from '@/services/entity.service'
import {
  addOrganizationEAPTemplate,
  getOrganizationEAPTemplate,
  updateOrganizationEAPTemplate,
} from '@/services/organization.service'
import { toast } from '@/utils/helpers'

const DEFAULT_QUESTIONS = [
  {
    id: 'activationMethod',
    label: 'How do you activate your emergency response?',
    isDefault: true,
    order: 0,
  },
  {
    id: 'hazardDescription',
    label: 'Hazard description:',
    isDefault: true,
    order: 1,
  },
  {
    id: 'extricationRequirements',
    label: 'Extrication Minimum Requirements:',
    isDefault: true,
    order: 2,
  },
  {
    id: 'emsRendezvous',
    label: 'EMS Rendezvous:',
    isDefault: true,
    order: 3,
  },
  {
    id: 'extricationEquipment',
    label: 'Extrication Equipment Required and Location of Equipment:',
    isDefault: true,
    order: 4,
  },
  {
    id: 'pointOfCare',
    label: 'Location of Point of Care:',
    isDefault: true,
    order: 5,
  },
  {
    id: 'pointOfFirstAid',
    label: 'Location of Point of First Aid:',
    isDefault: true,
    order: 6,
  },
  {
    id: 'pointOfAED',
    label: 'Location of Point of AED:',
    isDefault: true,
    order: 7,
  },
  {
    id: 'pointOfO2',
    label: 'Location of Point of O2:',
    isDefault: true,
    order: 8,
  },
  {
    id: 'extricationVideo',
    label: 'Video of the Extrication:',
    isDefault: true,
    order: 9,
  },
]

const DraggableQuestionRow = ({ index, onReorder, children }) => {
  const dropType = `eap-question`
  const [{ isOver }, drop] = useDrop({
    accept: dropType,
    drop({ index: draggedIndex }) {
      if (draggedIndex !== index) {
        onReorder(draggedIndex, index)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dropType,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index, dropType],
  )
  return (
    <div
      ref={drop}
      style={{
        opacity: isDragging ? 0.5 : 1,
        outline: isOver ? '2px dashed rgb(59 130 246)' : undefined,
        outlineOffset: isOver ? '-2px' : undefined,
        borderRadius: isOver ? '0.5rem' : undefined,
      }}
    >
      {children(drag)}
    </div>
  )
}

DraggableQuestionRow.propTypes = {
  index: PropTypes.number.isRequired,
  onReorder: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
}

const buildDefaultQuestion = () => ({
  text: '',
  jobs: [],
  quizQuestion: null,
})

export const EAPForm = observer(() => {
  const { entityId, eapId, organizationId } = useParams()
  const navigate = useNavigate()
  // Template mode (RSCX-320): the same form, but tied to an organization and
  // shared with facilities. Templates carry no jobs (facilities map jobs when
  // they adopt), so all job UI is hidden and writes go to the org endpoints.
  const isTemplate = !!organizationId
  const isEditMode = !!eapId && eapId !== 'new'
  const questionRefs = useRef({})
  const isNavigatingAfterSaveRef = useRef(false)
  const [showFileUploader, setShowFileUploader] = useState({})

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    quizEnabled: false,
    quizDueMonths: 6,
    jobs: [],
    questions: DEFAULT_QUESTIONS.map((q) => ({
      ...q,
      ...buildDefaultQuestion(),
    })),
  })

  const [originalFormData, setOriginalFormData] = useState(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const queryClient = useQueryClient()

  // GET Jobs — facilities only; templates have no jobs.
  const { data: jobsData } = useQuery({
    queryKey: ['accounts', 'jobs', entityId],
    queryFn: () =>
      getEntityJobs({
        entityId,
        sorting: [],
        pagination: { pageSize: 100, pageIndex: 0 },
      }),
    enabled: !isTemplate,
  })

  const jobs = jobsData?.results || []

  // GET EAP (if editing) — org template or facility EAP depending on mode.
  const { data: eapData, isPending: isLoadingEAP } = useQuery({
    queryKey: isTemplate
      ? ['organization-eap-templates', organizationId, eapId]
      : ['accounts', 'eaps', entityId, eapId],
    queryFn: () =>
      isTemplate
        ? getOrganizationEAPTemplate({ organizationId, eapId })
        : getEAP({ entityId, eapId }),
    enabled: isEditMode,
  })

  useEffect(() => {
    isNavigatingAfterSaveRef.current = false
  }, [eapId])

  // Load EAP data when editing
  useEffect(() => {
    if (eapData) {
      const transformedQuestions =
        eapData.questions?.map((q) => {
          let { filePath } = q
          if (!filePath && q.file) {
            const urlMatch = q.file.match(/\.com\/(.+?)(?:\?|$)/)
            if (urlMatch) {
              ;[, filePath] = urlMatch
            }
          }

          const quizQuestion = q.quizQuestion
            ? {
                id: q.quizQuestion.id,
                text: q.quizQuestion.text || '',
                answers: (q.quizQuestion.answers || []).map((a, answerIdx) => ({
                  id: `existing-answer-${q.id}-${answerIdx}`,
                  text: a.text,
                  isCorrect: a.isCorrect,
                })),
              }
            : null

          return {
            ...q,
            jobs: q.jobs || [],
            filePath: filePath || null,
            fileDisplayUrl: q.file || null,
            quizQuestion,
          }
        }) || DEFAULT_QUESTIONS

      const loadedData = {
        name: eapData.name || '',
        description: eapData.description || '',
        quizEnabled: eapData.quizEnabled || false,
        quizDueMonths: eapData.quizDueMonths || 6,
        jobs: eapData.jobs || [],
        questions: transformedQuestions,
      }

      setFormData(loadedData)
      setOriginalFormData(loadedData)
      setHasUnsavedChanges(false)
    }
  }, [eapData])

  useEffect(() => {
    if (!isEditMode && !originalFormData) {
      setOriginalFormData(formData)
    }
  }, [isEditMode, originalFormData, formData])

  useEffect(() => {
    if (originalFormData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData)
      setHasUnsavedChanges(hasChanges)
    }
  }, [formData, originalFormData])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges &&
      !isNavigatingAfterSaveRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const { isPending: isSaving, mutate: saveEAPMutation } = useMutation({
    mutationFn: (data) => {
      if (isTemplate) {
        return isEditMode
          ? updateOrganizationEAPTemplate({ organizationId, id: eapId, ...data })
          : addOrganizationEAPTemplate({ organizationId, ...data })
      }
      if (isEditMode) {
        return updateEAP({ entityId, id: eapId, ...data })
      }
      return addEAP({ entityId, ...data })
    },
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      const noun = isTemplate ? 'Shared EAP' : 'Emergency Action Plan'
      toast(`${noun} ${isEditMode ? 'updated' : 'created'} successfully`, 'success')

      setHasUnsavedChanges(false)
      setOriginalFormData(formData)

      if (isTemplate) {
        queryClient.invalidateQueries({ queryKey: ['organization-eap-templates', organizationId] })
        if (isEditMode) {
          queryClient.invalidateQueries({
            queryKey: ['organization-eap-templates', organizationId, eapId],
          })
        } else {
          isNavigatingAfterSaveRef.current = true
          navigate(`/organizations/${organizationId}/eap-templates/${data.id}`)
        }
        return
      }

      queryClient.invalidateQueries({ queryKey: ['accounts', 'eaps', entityId] })
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['accounts', 'eaps', entityId, eapId] })
      } else {
        isNavigatingAfterSaveRef.current = true
        navigate(`/accounts/${entityId}/eaps/${data.id}`)
      }
    },
  })

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleJobToggle = (jobId) => {
    setFormData((prev) => {
      const isAdding = !prev.jobs.includes(jobId)
      const newJobs = isAdding ? [...prev.jobs, jobId] : prev.jobs.filter((id) => id !== jobId)

      if (isAdding) {
        // Don't touch question.jobs on add: empty (= visible to all jobs with
        // EAP access) stays empty so the new job picks it up automatically,
        // and explicit per-question restrictions stay as the admin set them.
        return { ...prev, jobs: newJobs }
      }

      // On remove, drop the now-orphaned jobId from each question's
      // restriction list so we don't leave dangling references.
      return {
        ...prev,
        jobs: newJobs,
        questions: prev.questions.map((q) => ({
          ...q,
          jobs: (q.jobs || []).filter((id) => id !== jobId),
        })),
      }
    })
  }

  const handleQuestionLabelChange = (questionId, label) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === questionId ? { ...q, label } : q)),
    }))
  }

  const handleQuestionTextChange = (questionId, text) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => (q.id === questionId ? { ...q, text } : q)),
    }))
  }

  const handleQuestionJobToggle = (questionId, jobId) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId) {
          const questionJobs = q.jobs || []
          return {
            ...q,
            jobs: questionJobs.includes(jobId)
              ? questionJobs.filter((id) => id !== jobId)
              : [...questionJobs, jobId],
          }
        }
        return q
      }),
    }))
  }

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `custom-${Date.now()}`,
      label: 'New Question',
      isDefault: false,
      ...buildDefaultQuestion(),
      order: formData.questions.length,
    }
    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }))
    setTimeout(() => {
      questionRefs.current[newQuestion.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  const handleReorderQuestions = (fromIndex, toIndex) => {
    setFormData((prev) => {
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return prev
      if (fromIndex >= prev.questions.length || toIndex >= prev.questions.length) return prev
      const reordered = [...prev.questions]
      const [moved] = reordered.splice(fromIndex, 1)
      reordered.splice(toIndex, 0, moved)
      return {
        ...prev,
        questions: reordered.map((q, i) => ({ ...q, order: i })),
      }
    })
  }

  const handleDeleteQuestion = (questionId) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== questionId),
    }))
  }

  const handleFileUploaded = (questionId, uploadedFile) => {
    const filePath = uploadedFile.path

    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, filePath, fileDisplayUrl: null } : q,
      ),
    }))
    setShowFileUploader((prev) => ({ ...prev, [questionId]: false }))
    toast('File uploaded successfully. Save and reload to preview.', 'info')
  }

  const handleRemoveFile = (questionId) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, filePath: null, fileDisplayUrl: null } : q,
      ),
    }))
  }

  // Quiz Question Handlers
  const generateAnswerId = () =>
    `answer-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`

  const handleToggleQuizQuestion = (questionId) => {
    setFormData((prev) => {
      const newQuestions = prev.questions.map((q) => {
        if (q.id === questionId) {
          if (q.quizQuestion) {
            return { ...q, quizQuestion: null }
          }
          return {
            ...q,
            quizQuestion: {
              text: '',
              answers: [
                { id: generateAnswerId(), text: '', isCorrect: true },
                { id: generateAnswerId(), text: '', isCorrect: false },
              ],
            },
          }
        }
        return q
      })

      const validQuizCount = newQuestions.reduce(
        (count, q) => count + (isValidQuizQuestion(q.quizQuestion) ? 1 : 0),
        0,
      )

      const newQuizEnabled = validQuizCount === 0 ? false : prev.quizEnabled

      return {
        ...prev,
        questions: newQuestions,
        quizEnabled: newQuizEnabled,
      }
    })
  }

  const handleQuizQuestionTextChange = (questionId, text) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId && q.quizQuestion
          ? { ...q, quizQuestion: { ...q.quizQuestion, text } }
          : q,
      ),
    }))
  }

  const handleQuizAnswerTextChange = (questionId, answerIndex, text) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId && q.quizQuestion) {
          const newAnswers = [...q.quizQuestion.answers]
          newAnswers[answerIndex] = { ...newAnswers[answerIndex], text }
          return { ...q, quizQuestion: { ...q.quizQuestion, answers: newAnswers } }
        }
        return q
      }),
    }))
  }

  const handleQuizCorrectAnswerChange = (questionId, answerIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId && q.quizQuestion) {
          const newAnswers = q.quizQuestion.answers.map((a, idx) => ({
            ...a,
            isCorrect: idx === answerIndex,
          }))
          return { ...q, quizQuestion: { ...q.quizQuestion, answers: newAnswers } }
        }
        return q
      }),
    }))
  }

  const handleAddQuizAnswer = (questionId) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId && q.quizQuestion && q.quizQuestion.answers.length < 4) {
          return {
            ...q,
            quizQuestion: {
              ...q.quizQuestion,
              answers: [
                ...q.quizQuestion.answers,
                { id: generateAnswerId(), text: '', isCorrect: false },
              ],
            },
          }
        }
        return q
      }),
    }))
  }

  const handleRemoveQuizAnswer = (questionId, answerIndex) => {
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) => {
        if (q.id === questionId && q.quizQuestion && q.quizQuestion.answers.length > 2) {
          const newAnswers = q.quizQuestion.answers
            .filter((_, idx) => idx !== answerIndex)
            .map((a) => ({ ...a }))
          const hasCorrect = newAnswers.some((a) => a.isCorrect)
          if (!hasCorrect && newAnswers.length > 0) {
            newAnswers[0].isCorrect = true
          }
          return { ...q, quizQuestion: { ...q.quizQuestion, answers: newAnswers } }
        }
        return q
      }),
    }))
  }

  const isVideo = (url) => {
    if (!url) return false
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv']
    return videoExtensions.some((ext) => url.toLowerCase().includes(ext))
  }

  const isImage = (url) => {
    if (!url) return false
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']
    return imageExtensions.some((ext) => url.toLowerCase().includes(ext))
  }

  const isValidQuizQuestion = (qq) =>
    !!qq &&
    !!qq.text?.trim() &&
    qq.answers?.length >= 2 &&
    qq.answers.some((a) => a.isCorrect) &&
    qq.answers.every((a) => a.text?.trim())

  const handleSubmit = (e) => {
    e.preventDefault()

    const validQuizQuestions = formData.questions.reduce(
      (count, q) => count + (isValidQuizQuestion(q.quizQuestion) ? 1 : 0),
      0,
    )

    // Validate the reminder cadence — clamp to [1, 120] months. Empty/0/non-
    // numeric values surface a toast and abort instead of silently falling
    // back to a default (which can quietly change a saved cadence).
    const dueMonthsRaw = Number(formData.quizDueMonths)
    if (!Number.isFinite(dueMonthsRaw) || dueMonthsRaw < 1 || dueMonthsRaw > 120) {
      toast('Reminder interval must be a whole number of months between 1 and 120.', 'error')
      return
    }
    const quizDueMonths = Math.round(dueMonthsRaw)

    const shouldEnableQuiz = formData.quizEnabled && validQuizQuestions > 0

    const apiData = {
      name: formData.name,
      description: formData.description,
      quizEnabled: shouldEnableQuiz,
      quizDueMonths,
      jobs: formData.jobs.filter((id) => id !== null),
      questions: formData.questions.map((q) => {
        const apiQuestion = {
          id: q.id,
          label: q.label,
          isDefault: q.isDefault,
          order: q.order,
          file: q.filePath || null,
          text: q.text || '',
          jobs: (q.jobs || []).filter((id) => id !== null),
        }

        if (isValidQuizQuestion(q.quizQuestion)) {
          apiQuestion.quizQuestion = {
            id: q.quizQuestion.id,
            text: q.quizQuestion.text,
            answers: q.quizQuestion.answers.map((a) => ({
              text: a.text,
              isCorrect: a.isCorrect,
            })),
          }
        } else {
          apiQuestion.quizQuestion = null
        }

        return apiQuestion
      }),
    }

    saveEAPMutation(apiData)
  }

  const availableJobs =
    formData.jobs.length > 0 ? jobs.filter((job) => formData.jobs.includes(job.id)) : jobs

  const quizQuestionCount = formData.questions.reduce(
    (count, q) => count + (isValidQuizQuestion(q.quizQuestion) ? 1 : 0),
    0,
  )

  const renderQuizEditor = (question) => {
    const { quizQuestion } = question

    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="mb-3 space-y-2">
          <Label className="text-xs text-gray-600">Quiz Question Text</Label>
          <Input
            value={quizQuestion.text}
            onChange={(e) => handleQuizQuestionTextChange(question.id, e.target.value)}
            placeholder="Enter the quiz question..."
            className="bg-white"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-gray-600">Answer Options (select the correct one)</Label>
          <RadioGroup
            value={String(quizQuestion.answers.findIndex((a) => a.isCorrect))}
            onValueChange={(value) => handleQuizCorrectAnswerChange(question.id, Number(value))}
            className="gap-2"
          >
            {quizQuestion.answers.map((answer, idx) => {
              const optionLabel = `Option ${idx + 1}`
              return (
                <div key={answer.id} className="flex items-center gap-2">
                  <RadioGroupItem
                    value={String(idx)}
                    className="bg-white"
                    aria-label={`Mark ${answer.text || optionLabel} as correct answer`}
                  />
                  <Input
                    value={answer.text}
                    onChange={(e) => handleQuizAnswerTextChange(question.id, idx, e.target.value)}
                    placeholder={optionLabel}
                    className="flex-1 bg-white"
                  />
                  {quizQuestion.answers.length > 2 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveQuizAnswer(question.id, idx)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )
            })}
          </RadioGroup>
        </div>

        {quizQuestion.answers.length < 4 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleAddQuizAnswer(question.id)}
            className="mt-2"
          >
            <Plus className="mr-1 h-3 w-3" />
            Add Answer Option
          </Button>
        )}

        <p className="mt-2 text-xs text-gray-500">
          {quizQuestion.answers.length}/4 options (min 2, max 4). Select the radio button for the
          correct answer.
        </p>
      </div>
    )
  }

  const renderQuestion = (question, dragHandleRef) => (
    <div
      key={question.id}
      ref={(el) => {
        questionRefs.current[question.id] = el
      }}
      className="rounded-lg border border-gray-200 p-4"
    >
      <div className="mb-3 flex items-center gap-2">
        <div
          ref={dragHandleRef}
          className="cursor-move touch-none"
          aria-label="Drag to reorder question"
        >
          <GripVertical className="h-4 w-4 text-gray-400" />
        </div>
        <Input
          value={question.label}
          onChange={(e) => handleQuestionLabelChange(question.id, e.target.value)}
          className="flex-1"
          placeholder="Question label"
        />
        {!question.isDefault && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteQuestion(question.id)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Textarea
        value={question.text || ''}
        onChange={(e) => handleQuestionTextChange(question.id, e.target.value)}
        rows={3}
        className="mb-3 resize-none"
        placeholder="Answer text"
      />

      {/* File Upload */}
      <div className="mb-3">
        {(() => {
          if (question.filePath) {
            return (
              <div className="relative rounded border border-gray-200 bg-gray-50 p-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute top-1 right-1 z-10 bg-white shadow-sm hover:bg-gray-100"
                  onClick={() => handleRemoveFile(question.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
                {question.fileDisplayUrl ? (
                  (() => {
                    const displayUrl = question.fileDisplayUrl
                    if (isImage(displayUrl)) {
                      return (
                        <img
                          src={displayUrl}
                          alt="Uploaded content"
                          className="max-h-96 w-full rounded object-contain"
                        />
                      )
                    }
                    if (isVideo(displayUrl)) {
                      return (
                        <video src={displayUrl} controls className="max-h-96 w-full rounded">
                          <track kind="captions" />
                          Your browser does not support the video tag.
                        </video>
                      )
                    }
                    return null
                  })()
                ) : (
                  <div className="flex items-center gap-2 p-3 text-sm text-gray-600">
                    <Upload className="h-4 w-4" />
                    <span>File uploaded. Save and reload to preview.</span>
                  </div>
                )}
              </div>
            )
          }

          if (showFileUploader[question.id]) {
            return (
              <div className="space-y-2">
                <FileUploader
                  filePrefix="eaps"
                  handleUploadToServer={async (fileToUpload) => {
                    const filePath =
                      fileToUpload.fileUploadName || fileToUpload.fileName || fileToUpload.name

                    if (filePath) {
                      handleFileUploaded(question.id, { path: filePath })
                    } else {
                      // eslint-disable-next-line no-console
                      console.error('Could not determine file path from:', fileToUpload)
                      toast('File uploaded but path could not be determined', 'error')
                    }
                  }}
                  acceptedFileTypes={['image/*', 'video/*']}
                  maxFiles={1}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setShowFileUploader((prev) => ({ ...prev, [question.id]: false }))
                  }
                >
                  Cancel
                </Button>
              </div>
            )
          }

          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowFileUploader((prev) => ({ ...prev, [question.id]: true }))}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload File
            </Button>
          )
        })()}
      </div>

      {/* Job Access — facility-only; template questions carry no jobs (RSCX-320). */}
      {!isTemplate && (
        <div className="border-t border-gray-200 pt-3">
          <p className="mb-2 text-xs text-gray-600">Jobs that can access this question:</p>
          <div className="space-y-2">
            {availableJobs.map((job) => (
              <label key={job.id} className="flex cursor-pointer items-center gap-2">
                <Checkbox
                  checked={question.jobs?.includes(job.id) || false}
                  onCheckedChange={() => handleQuestionJobToggle(question.id, job.id)}
                />
                <span className="text-sm">{job.title}</span>
              </label>
            ))}
          </div>
          {(!question.jobs || question.jobs.length === 0) && (
            <p className="mt-2 text-xs text-gray-500">
              No jobs selected - will be available to all jobs with EAP access
            </p>
          )}
        </div>
      )}

      {/* Quiz Question Editor */}
      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="mb-3 flex items-center gap-2">
          <Checkbox
            id={`quiz-toggle-${question.id}`}
            checked={!!question.quizQuestion}
            onCheckedChange={() => handleToggleQuizQuestion(question.id)}
          />
          <label
            htmlFor={`quiz-toggle-${question.id}`}
            className="flex cursor-pointer items-center gap-1 text-sm font-medium text-gray-700"
          >
            <HelpCircle className="h-4 w-4" />
            Add Quiz Question
          </label>
        </div>

        {question.quizQuestion && renderQuizEditor(question)}
      </div>
    </div>
  )

  return (
    <>
      {blocker.state === 'blocked' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-3 text-lg font-semibold text-gray-900">Unsaved Changes</h2>
            <p className="mb-6 text-gray-600">
              You have unsaved changes. Are you sure you want to leave this page?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => blocker.reset()}>
                Stay
              </Button>
              <Button variant="destructive" onClick={() => blocker.proceed()}>
                Leave
              </Button>
            </div>
          </div>
        </div>
      )}

      <Layout
        breadcrumbs={
          isTemplate
            ? [
                'Organization',
                {
                  pathname: `/organizations/${organizationId}/eap-templates`,
                  title: 'Shared EAPs',
                },
                isEditMode ? 'Edit' : 'Create',
              ]
            : [
                'Admin',
                { pathname: `/accounts/${entityId}/eaps`, title: 'Emergency Action Plans' },
                isEditMode ? 'Edit' : 'Create',
              ]
        }
      >
        <Layout.Heading
          title={(() => {
            if (isTemplate) return isEditMode ? 'Edit Shared EAP' : 'Create Shared EAP'
            return isEditMode ? 'Edit Emergency Action Plan' : 'Create New Emergency Action Plan'
          })()}
        />

        <div className="mt-1 flex shrink-0 flex-row items-start justify-start gap-4 sm:justify-end">
          {/* Preview + Compliance are facility-only: templates aren't taken by members. */}
          {!isTemplate && isEditMode && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/accounts/${entityId}/eaps/${eapId}/preview`)}
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview EAP
            </Button>
          )}
          {!isTemplate && isEditMode && formData.quizEnabled && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/accounts/${entityId}/eaps/${eapId}/compliance`)}
            >
              <ClipboardCheck className="mr-2 h-4 w-4" />
              View Compliance
            </Button>
          )}
          <Button type="submit" form="eap-form" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {isEditMode && isLoadingEAP ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="pb-12">
            <form id="eap-form" onSubmit={handleSubmit} className="flex flex-col gap-8">
              {/* General */}
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                <span className="text-muted-foreground font-semibold sm:w-1/4">General</span>
                <div className="flex w-full flex-col gap-8">
                  <div className="space-y-2">
                    <Label>Name*</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Enter EAP name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={3}
                      className="resize-none"
                      placeholder="Enter description"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Job Access — facility-only; templates carry no jobs (RSCX-320). */}
              {!isTemplate && (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                    <div className="sm:w-1/4">
                      <span className="text-muted-foreground font-semibold">Job Access</span>
                      <p className="text-muted-foreground text-xs">
                        Select which jobs have access to this EAP
                      </p>
                    </div>
                    <div className="flex w-full flex-col gap-4">
                      <div className="space-y-2">
                        {jobs.map((job) => (
                          <label key={job.id} className="flex cursor-pointer items-center gap-2">
                            <Checkbox
                              checked={formData.jobs.includes(job.id)}
                              onCheckedChange={() => handleJobToggle(job.id)}
                            />
                            <span className="text-sm">{job.title}</span>
                          </label>
                        ))}
                      </div>
                      {formData.jobs.length === 0 && (
                        <p className="text-muted-foreground text-xs">
                          No jobs selected - will be available to all jobs
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />
                </>
              )}

              {/* Quiz Settings */}
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                <div className="sm:w-1/4">
                  <span className="text-muted-foreground font-semibold">Quiz Settings</span>
                  <p className="text-muted-foreground text-xs">
                    Configure quiz requirements for this EAP
                  </p>
                </div>
                <div className="flex w-full flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="quiz-enabled"
                      checked={formData.quizEnabled}
                      onCheckedChange={(checked) => {
                        if (checked && quizQuestionCount === 0) {
                          toast('Add quiz questions below before enabling the quiz', 'warning')
                          return
                        }
                        handleChange('quizEnabled', checked)
                      }}
                      disabled={quizQuestionCount === 0 && !formData.quizEnabled}
                    />
                    <div>
                      <Label
                        htmlFor="quiz-enabled"
                        className={`cursor-pointer ${quizQuestionCount === 0 && !formData.quizEnabled ? 'text-muted-foreground' : ''}`}
                      >
                        Enable Quiz
                        {quizQuestionCount > 0 && (
                          <span className="text-muted-foreground ml-2 text-xs">
                            ({quizQuestionCount} question
                            {quizQuestionCount !== 1 ? 's' : ''})
                          </span>
                        )}
                      </Label>
                      <p className="text-muted-foreground text-xs">
                        {quizQuestionCount === 0
                          ? 'Add quiz questions below to enable'
                          : `When enabled, users will be prompted to retake the quiz every ${formData.quizDueMonths} month${formData.quizDueMonths === 1 ? '' : 's'}.`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Label htmlFor="quiz-due-months" className="w-32 text-sm">
                      Reminder interval
                    </Label>
                    <Input
                      id="quiz-due-months"
                      type="number"
                      min={1}
                      max={120}
                      step={1}
                      value={formData.quizDueMonths}
                      onChange={(e) =>
                        handleChange(
                          'quizDueMonths',
                          e.target.value === '' ? '' : Number(e.target.value),
                        )
                      }
                      onBlur={(e) => {
                        const v = Number(e.target.value)
                        if (!Number.isFinite(v) || v < 1) handleChange('quizDueMonths', 1)
                        else if (v > 120) handleChange('quizDueMonths', 120)
                      }}
                      className="w-24"
                      disabled={!formData.quizEnabled && quizQuestionCount === 0}
                    />
                    <span className="text-muted-foreground text-sm">
                      month{formData.quizDueMonths === 1 ? '' : 's'} between retakes
                    </span>
                  </div>

                  {formData.quizEnabled && quizQuestionCount === 0 && (
                    <p className="text-xs text-red-600">
                      Warning: Quiz is enabled but no valid quiz questions are configured. Add quiz
                      questions below before saving.
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Questions */}
              <div className="flex flex-col gap-2 sm:flex-row sm:gap-8">
                <div className="sm:w-1/4">
                  <span className="text-muted-foreground font-semibold">Questions</span>
                  <p className="text-muted-foreground text-xs">
                    Define the EAP questions. Each question can be restricted to specific jobs and
                    can have an optional quiz question.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={handleAddQuestion}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add Question
                  </Button>
                </div>
                <div className="flex w-full flex-col gap-4">
                  <div className="space-y-4">
                    {formData.questions.map((question, idx) => (
                      <DraggableQuestionRow
                        key={question.id}
                        index={idx}
                        onReorder={handleReorderQuestions}
                      >
                        {(dragRef) => renderQuestion(question, dragRef)}
                      </DraggableQuestionRow>
                    ))}
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </Layout>
    </>
  )
})
